/* Progressive enhancement for the dedicated whole-house landing page. */
(function () {
  'use strict';
  const rootSelector = '#cq-quiz-v3[data-cqq-quiz="wh"]';
  const mounted = new WeakMap();

  function mount(root) {
    if (mounted.has(root)) return;
    const rooms = root.querySelector('[data-cqq-rooms]');
    const toolbar = rooms && rooms.querySelector('.cqq-room-tabs');
    if (!toolbar) return;
    const tabs = Array.from(toolbar.querySelectorAll('[data-cqq-room-tab]'));
    const availablePanels = Array.from(rooms.querySelectorAll('[data-cqq-room-panel]'));
    const panels = tabs.map(function (tab) {
      return availablePanels.find(function (panel) {
        return panel.id === tab.getAttribute('aria-controls');
      });
    });
    // Incomplete markup keeps the readable, unenhanced version intact.
    if (!tabs.length || tabs.some(function (tab) { return !tab.id; }) ||
        new Set(tabs.map(function (tab) { return tab.id; })).size !== tabs.length ||
        panels.some(function (panel) { return !panel; }) ||
        new Set(panels).size !== tabs.length || panels.length !== availablePanels.length) return;

    const controller = new AbortController();
    const options = {signal: controller.signal};

    function select(index, focus) {
      tabs.forEach(function (tab, i) {
        tab.setAttribute('aria-selected', String(i === index));
        tab.tabIndex = i === index ? 0 : -1;
        panels[i].hidden = i !== index;
      });
      if (focus) tabs[index].focus();
    }

    toolbar.setAttribute('role', 'tablist');
    toolbar.setAttribute('aria-orientation', 'horizontal');
    tabs.forEach(function (tab, i) {
      tab.setAttribute('role', 'tab');
      panels[i].setAttribute('role', 'tabpanel');
      panels[i].setAttribute('aria-labelledby', tab.id);
      panels[i].tabIndex = 0;
    });
    select(0, false);
    rooms.classList.add('is-enhanced');
    toolbar.hidden = false;

    toolbar.addEventListener('click', function (event) {
      const index = tabs.indexOf(event.target.closest('[data-cqq-room-tab]'));
      if (index !== -1) select(index, true);
    }, options);

    toolbar.addEventListener('keydown', function (event) {
      const current = tabs.indexOf(event.target.closest('[data-cqq-room-tab]'));
      if (current === -1) return;
      if (!['Home', 'End', 'ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      // The shared quiz handler assumes every tablist belongs to a dialog.
      // Keep room keys local, without cancelling native modified shortcuts.
      event.stopPropagation();
      if (event.altKey || event.ctrlKey || event.metaKey) return;
      let next;
      if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = tabs.length - 1;
      else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        const direction = window.getComputedStyle(toolbar).direction === 'rtl' ? -1 : 1;
        const step = event.key === 'ArrowRight' ? direction : -direction;
        next = (current + step + tabs.length) % tabs.length;
      } else return;
      event.preventDefault();
      select(next, true);
    }, options);

    mounted.set(root, function () {
      controller.abort();
      toolbar.hidden = true;
      toolbar.removeAttribute('role');
      toolbar.removeAttribute('aria-orientation');
      rooms.classList.remove('is-enhanced');
      tabs.forEach(function (tab, i) {
        tab.removeAttribute('role');
        tab.removeAttribute('aria-selected');
        tab.removeAttribute('tabindex');
        panels[i].hidden = false;
        panels[i].removeAttribute('role');
        panels[i].removeAttribute('aria-labelledby');
        panels[i].removeAttribute('tabindex');
      });
      mounted.delete(root);
    });
  }

  // Quiet entrance for the landing sections (polish pass 2026-10-11): each block fades up 16 px once, the first time it
  // reaches the reader, with a short stagger inside a row. Opacity and transform only, so nothing moves the layout.
  // Without IntersectionObserver, or with reduced motion, nothing is hidden. The first view is never animated.
  const revealed = new WeakSet();
  const REVEAL_GROUPS = [
    '.cqq-sale-section-head', '.cqq-room-explorer', '.cqq-path-stage', '.cqq-brand-layout>div:first-child',
    '.cqq-brand-proof>div', '.cqq-needs-grid>*', '.cqq-sale-product', '.cqq-shop-footer', '.cqq-sale-quotes>figure',
    '.cqq-sale-support', '.cqq-questions-layout>div', '.cqq-close-layout>*'
  ];
  function reveal(root) {
    if (revealed.has(root)) return;
    const sales = root.querySelector('.cqq-sales');
    if (!sales || !('IntersectionObserver' in window)) return;
    if (window.matchMedia && !window.matchMedia('(prefers-reduced-motion: no-preference)').matches) return;
    revealed.add(root);
    const items = [];
    REVEAL_GROUPS.forEach(function (selector) {
      sales.querySelectorAll(selector).forEach(function (el) {
        if (el.hasAttribute('data-cqq-rv')) return;
        const siblings = el.parentElement ? Array.from(el.parentElement.children).filter(function (c) { return c.matches(selector); }) : [el];
        el.setAttribute('data-cqq-rv', '');
        el.style.setProperty('--cqq-rv-d', Math.min(siblings.indexOf(el), 3) * 90 + 'ms');
        items.push(el);
      });
    });
    const show = function (el) {
      el.classList.add('is-in');
      const delay = parseFloat(el.style.getPropertyValue('--cqq-rv-d')) || 0;
      window.setTimeout(function () { el.classList.add('is-done'); }, 800 + delay);
    };
    const io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        io.unobserve(entry.target);
        show(entry.target);
      });
    }, {threshold: 0, rootMargin: '0px 0px -32px 0px'});
    // A block already on screen at this moment (a short, wide window or a restored scroll position) stays as it is:
    // it is never hidden and faded back in.
    const fold = window.innerHeight || document.documentElement.clientHeight;
    items.forEach(function (el) {
      const box = el.getBoundingClientRect();
      if (box.top < fold && box.bottom > 0) { el.classList.add('is-in', 'is-done'); return; }
      io.observe(el);
    });
    sales.classList.add('is-rv');
    // Printing or a long-hidden tab never leaves a block invisible.
    window.addEventListener('beforeprint', function () { items.forEach(show); });
  }

  function visit(scope, callback) {
    if (scope.matches && scope.matches(rootSelector)) callback(scope);
    scope.querySelectorAll(rootSelector).forEach(callback);
  }

  function init(scope) { visit(scope, mount); visit(scope, reveal); }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); }, {once: true});
  } else init(document);
  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
  document.addEventListener('shopify:section:unload', function (event) {
    visit(event.target, function (root) {
      const unmount = mounted.get(root);
      if (unmount) unmount();
    });
  });
})();
